import { type ReactNode, useEffect, useRef, useSyncExternalStore } from 'react';

// The event side panel renders outside the chronology page tree and its content is
// built once when it opens, so the active search is shared through this store.
// Several panels can be mounted at once (the page, and an entity's chronology in
// the side panel). Each registers its own query; a blank one does not wipe another.
type HighlightRegistration = { id: number; query: string };

let nextHighlightId = 1;
let highlightRegistrations: HighlightRegistration[] = [];
let activeQuery = '';
const listeners = new Set<() => void>();

function publishedQuery(): string {
  for (let index = highlightRegistrations.length - 1; index >= 0; index--) {
    const query = highlightRegistrations[index].query;
    if (query) return query;
  }
  return '';
}

function publishHighlight() {
  const next = publishedQuery();
  if (next === activeQuery) return;
  activeQuery = next;
  for (const listener of listeners) listener();
}

export function useChronologySearchHighlightSource(query: string) {
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextHighlightId++;
  const trimmed = query.trim();

  useEffect(() => {
    const id = idRef.current;
    highlightRegistrations = [
      ...highlightRegistrations.filter(entry => entry.id !== id),
      { id, query: trimmed },
    ];
    publishHighlight();
    return () => {
      highlightRegistrations = highlightRegistrations.filter(entry => entry.id !== id);
      publishHighlight();
    };
  }, [trimmed]);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useChronologySearchHighlight(): string {
  return useSyncExternalStore(subscribe, () => activeQuery, () => '');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Marks case-insensitive occurrences of `query`, the same matching the chronology search uses. */
export function highlightSearchText(text: string, query: string): ReactNode {
  if (!query || !text) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  if (parts.length === 1) return text;
  const needle = query.toLowerCase();
  return parts.map((part, index) =>
    part.toLowerCase() === needle ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional.
      <mark key={index} className="rounded-sm bg-yellow-200 px-px text-inherit">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export function SearchHighlightedText({ text }: { text: string }) {
  const query = useChronologySearchHighlight();
  return <>{highlightSearchText(text, query)}</>;
}
